import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
    Grid,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Stack,
    Button
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Loader } from '../../../../Components';
import { styled, alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import {cleanName} from "../../../../Setup/Config/formats";
import {fetchCurrentRoster} from "../../../Roster/Roster/Redux/Action";
import { usePermissions } from '../../../../Hooks/usePermissions';

// Microsoft Fluent Design inspired table container - enhanced, mobile optimized
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: 0,
    // Scrollport for Table stickyHeader — both axes so header stays top while body scrolls
    maxHeight: 'min(72vh, 640px)',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
        maxHeight: 'min(68vh, 520px)',
    },
    '& .MuiTableCell-root': {
        padding: theme.spacing(2, 1.75),
        fontSize: '0.875rem',
        textAlign: 'center',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        lineHeight: 1.5,
        '&:first-of-type': {
            paddingLeft: theme.spacing(3)
        },
        '&:last-of-type': {
            paddingRight: theme.spacing(3)
        },
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 1),
            fontSize: '0.8125rem',
            '&:first-of-type': { paddingLeft: theme.spacing(2) },
            '&:last-of-type': { paddingRight: theme.spacing(2) }
        }
    },
    '& .MuiTableHead-root': {
        '& .MuiTableCell-root': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            fontWeight: 700,
            color: theme.palette.text.primary,
            textTransform: 'none',
            letterSpacing: '0.02em',
            fontSize: '0.875rem',
            padding: theme.spacing(1.75, 1.75),
            borderBottom: `2px solid ${theme.palette.grey[300]}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backdropFilter: 'blur(8px)',
            '&:nth-of-type(1)': { minWidth: 220, [theme.breakpoints.down('sm')]: { minWidth: 120 } },
            '&:nth-of-type(2)': { minWidth: 200, [theme.breakpoints.down('sm')]: { minWidth: 90 } },
            '&:nth-of-type(3)': { minWidth: 130, [theme.breakpoints.down('sm')]: { minWidth: 64 } },
            '&:nth-of-type(4)': { minWidth: 160, [theme.breakpoints.down('sm')]: { minWidth: 80 } },
            '&:first-of-type': { paddingLeft: theme.spacing(3) },
            '&:last-of-type': { paddingRight: theme.spacing(3) },
            [theme.breakpoints.down('sm')]: {
                padding: theme.spacing(1, 1),
                fontSize: '0.75rem',
                '&:first-of-type': { paddingLeft: theme.spacing(2) },
                '&:last-of-type': { paddingRight: theme.spacing(2) }
            }
        }
    },
    '& .dept-row': {
        backgroundColor: theme.palette.grey[50],
        '& .MuiTableCell-root': {
            backgroundColor: theme.palette.grey[50],
            fontWeight: 600,
            fontSize: '0.875rem',
            textAlign: 'left',
            color: theme.palette.text.primary,
            padding: theme.spacing(2, 3),
            borderBottom: `2px solid ${theme.palette.grey[200]}`,
            borderTop: `2px solid ${theme.palette.grey[200]}`,
            textTransform: 'none',
            letterSpacing: '0.01em',
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '3px',
                backgroundColor: theme.palette.primary.main,
                borderRadius: '0 2px 2px 0'
            },
            [theme.breakpoints.down('sm')]: {
                padding: theme.spacing(1.25, 2),
                fontSize: '0.8125rem'
            }
        }
    },
    '& .name-cell': {
        textAlign: 'left',
        fontWeight: 500,
        minWidth: 220,
        color: theme.palette.text.primary,
        fontSize: '0.875rem',
        lineHeight: 1.6,
        [theme.breakpoints.down('sm')]: {
            minWidth: 120,
            fontSize: '0.8125rem'
        }
    },
    '& .muted': {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.5,
        [theme.breakpoints.down('sm')]: { fontSize: '0.8125rem' }
    },
    '& .day-col': {
        minWidth: 110,
        fontWeight: 500,
        [theme.breakpoints.down('sm')]: { minWidth: 72 }
    },
    '& .MuiTableRow-root:hover': {
        backgroundColor: theme.palette.grey[50],
        transition: 'background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
        '& .MuiTableCell-root': {
            borderBottomColor: theme.palette.grey[300]
        }
    },
    '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
        borderBottom: 'none'
    }
}));

// Microsoft Fluent Design status pill - enhanced with better sizing
const StatusPill = styled('span')(({ theme, color }) => {
    const map = {
        success: {
            color: '#107C10', // Microsoft green
            bg: '#E8F5E9',
            border: 'none'
        },
        error: {
            color: '#D13438', // Microsoft red
            bg: '#FDE7E9',
            border: 'none'
        },
        warning: {
            color: '#FF8C00', // Microsoft orange
            bg: '#FFF4E6',
            border: 'none'
        },
        muted: {
            color: theme.palette.text.secondary,
            bg: theme.palette.grey[100],
            border: 'none'
        }
    };
    const picked = map[color] || map.muted;
    return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(0.5, 1.25),
        borderRadius: theme.spacing(0.75),
        fontWeight: 500,
        fontSize: '0.8125rem',
        color: picked.color,
        backgroundColor: picked.bg,
        border: picked.border,
        minWidth: '70px',
        [theme.breakpoints.down('sm')]: { minWidth: '52px', padding: theme.spacing(0.35, 0.75), fontSize: '0.6875rem' },
        whiteSpace: 'nowrap',
        transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
        lineHeight: 1.4,
        letterSpacing: '0.01em'
    };
});

// Microsoft Fluent Design card - enhanced with better shadows
const StyledCard = styled(Card)(({ theme }) => ({
    width: '100%',
    maxWidth: '1400px',
    borderRadius: theme.spacing(1.5),
    boxShadow: `0 1.6px 3.6px ${theme.palette.grey[300]}20, 0 0.3px 0.9px ${theme.palette.grey[300]}15`,
    border: `1px solid ${theme.palette.grey[200]}`,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        boxShadow: `0 3.2px 7.2px ${theme.palette.grey[300]}30, 0 0.6px 1.8px ${theme.palette.grey[300]}25`,
        borderColor: theme.palette.grey[300]
    }
}));

// Microsoft Fluent Design card header - responsive
const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(2, 2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: theme.spacing(3, 3)
    },
    '& .MuiCardHeader-content': {
        minWidth: 0,
        flex: '1 1 auto'
    },
    '& .MuiCardHeader-title': {
        flex: '1 1 auto',
        minWidth: 0
    },
    '& .MuiCardHeader-action': {
        margin: 0,
        marginTop: theme.spacing(0.5),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(1.5),
            alignSelf: 'stretch'
        },
        [theme.breakpoints.up('sm')]: {
            alignSelf: 'flex-start'
        }
    }
}));

export const DashboardGeneral = memo(() => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {hasPermission} = usePermissions();
    // State Management
    const [isLoading, setIsLoading] = useState(false);
    
    // Redux Selectors
    const departments = useSelector((store) => store.app.config.departments);
    const currentUser = useSelector((store) => store.auth.user);
    const roster = useSelector((store) => store.rosters.selected);
    const items = useSelector((store) => store.rosters.selected.entries);

    // Current week calculation (always current week)
    const currentWeek = useMemo(() => {
        const startOfWeek = dayjs().startOf('week').add(1, 'day'); // Monday
        const endOfWeek = dayjs().endOf('week').add(1, 'day'); // Sunday
        return { start: startOfWeek, end: endOfWeek };
    }, []);

    // Computed roster status from actual data
    const rosterStatus = useMemo(() => {
        return roster?.status?.name || 'DRAFT';
    }, [roster]);

    // Week days generation - Show only days with roster entries
    const weekDays = useMemo(() => {
        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }
        
        // Extract unique dates from roster entries
        const uniqueDates = [...new Set(items.map(item => item.roster_date))];
        
        // Convert to dayjs objects and sort
        const days = uniqueDates
            .map(date => dayjs(date))
            .sort((a, b) => a.isAfter(b) ? 1 : -1);
        
        return days;
    }, [items]);

    // Filter departments based on user role
    const availableDepartments = useMemo(() => {
        if (!departments || !Array.isArray(departments)) {
            return [];
        }
        if (currentUser?.role_type === 'MANAGER') {
            return departments.filter(dept => dept.manager_id === currentUser.id);
        }
        return departments;
    }, [departments, currentUser]);

    // Fetch roster data for current week
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        await fetchCurrentRoster({ dispatch }).then((res) => {
            setIsLoading(false);
        });
    }, [dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Format time from HH:mm:ss to HHmm format
    const formatTime = (timeString) => {
        if (!timeString) return '';
        return timeString.substring(0, 5).replace(':', '');
    };

    // Get status color for chip
    const getStatusColor = (status) => {
        const colorMap = {
            'DRAFT': 'warning',
            'LOCKED': 'info',
            'PDF_GENERATED': 'success',
            'PDF_FAILED': 'error'
        };
        return colorMap[status] || 'default';
    };

    // Get status meta for status pills
    const getStatusMeta = (status, rosterEntry) => {
        if (!status || status === '-') {
            return { label: '-', color: 'muted' };
        }
        const normalized = status.toLowerCase();
        if (normalized === 'off' || normalized === 'off_duty') {
            return { label: 'Off', color: 'error' };
        }
        if (normalized === 'leave') {
            return { label: 'Leave', color: 'warning' };
        }
        if (normalized === 'on_duty' && rosterEntry?.start_time && rosterEntry?.end_time) {
            return { 
                label: `${formatTime(rosterEntry.start_time)} - ${formatTime(rosterEntry.end_time)}`, 
                color: 'success' 
            };
        }
        return { label: cleanName(status), color: 'success' };
    };

    return (
        <Grid container spacing={3} sx={{ py: 3, px: { xs: 2, md: 4 } }}>
            <Grid item xs={12} display="flex" justifyContent="center">
                <StyledCard>
                    <StyledCardHeader
                        title={
                            <Stack spacing={0.5}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        sx={{
                                            letterSpacing: '-0.01em',
                                            lineHeight: 1.3,
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}
                                    >
                                        Current Week Roster Overview
                                    </Typography>
                                    <Chip
                                        label={cleanName(rosterStatus)}
                                        color={getStatusColor(rosterStatus)}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            height: '26px',
                                            borderWidth: '1px',
                                            borderColor: getStatusColor(rosterStatus) === 'info' ? 'primary.main' : undefined,
                                            backgroundColor: 'transparent',
                                            '& .MuiChip-label': { px: 1.5 }
                                        }}
                                    />
                                    {isLoading && (
                                        <Loader isLoading={isLoading} type="linear" height={2} />
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, fontWeight: 400 }}>
                                    {`${currentWeek.start.format('MMM DD')} - ${currentWeek.end.format('MMM DD, YYYY')}`}
                                </Typography>
                            </Stack>
                        }
                        action={
                            hasPermission('core.dashboard.management') && (
                                <Button
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => navigate('/dashboard')}
                                    variant="contained"
                                    color="warning"
                                    sx={(theme) => ({
                                        width: '100%',
                                        justifyContent: 'center',
                                        [theme.breakpoints.up('sm')]: { width: 'auto' }
                                    })}
                                >
                                    Back to Dashboard
                                </Button>
                            )
                        }
                    />
                    <CardContent sx={{ p: '0 !important', overflow: 'hidden' }}>
                        <StyledTableContainer>
                            <Table size="medium" stickyHeader sx={{ minWidth: 720 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">
                                            <Typography variant="caption" fontWeight={600} sx={{ color: 'text.secondary', letterSpacing: '0.01em' }}>
                                                Name
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                            <Typography variant="caption" fontWeight={600} sx={{ color: 'text.secondary', letterSpacing: '0.01em' }}>
                                                Duty / Services
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="caption" fontWeight={600} sx={{ color: 'text.secondary', letterSpacing: '0.01em' }}>
                                                Contact
                                            </Typography>
                                        </TableCell>
                                        {weekDays.map((day) => (
                                            <TableCell key={day.format('YYYY-MM-DD')} className="day-col" align="center">
                                                <Box>
                                                    <Typography variant="caption" fontWeight={600} sx={{ color: 'text.secondary', display: 'block', letterSpacing: '0.01em' }}>
                                                        {day.format('ddd')}
                                                    </Typography>
                                                    <Typography variant="caption" fontWeight={500} sx={{ color: 'text.secondary', display: 'block', opacity: 0.7, fontSize: '0.6875rem' }}>
                                                        {day.format('DD')}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        // Get all departments from config
                                        const allDepartments = (availableDepartments || []).map(dept => dept.department_name).sort();
                                        
                                        // Group roster entries by user and then by department
                                        const userRosterMap = {};
                                        if (items && items.length > 0) {
                                            items.forEach(entry => {
                                                const userId = entry.user_id;
                                                if (!userRosterMap[userId]) {
                                                    userRosterMap[userId] = {
                                                        user: entry.user,
                                                        rosters: {}
                                                    };
                                                }
                                                // Map roster entries by date
                                                userRosterMap[userId].rosters[entry.roster_date] = {
                                                    status: entry.status,
                                                    start_time: entry.start_time,
                                                    end_time: entry.end_time,
                                                    event: entry.event,
                                                    venue: entry.venue,
                                                    location: entry.location
                                                };
                                            });
                                        }

                                        // Group users by department
                                        const departmentGroups = {};
                                        Object.values(userRosterMap).forEach(userData => {
                                            const deptName = userData.user.department_id ? 
                                                availableDepartments.find(d => d.id === userData.user.department_id)?.department_name || 'No Department' : 
                                                'No Department';
                                            
                                            if (!departmentGroups[deptName]) {
                                                departmentGroups[deptName] = [];
                                            }
                                            departmentGroups[deptName].push(userData);
                                        });

                                        const rows = [];
                                        
                                        // Create rows for ALL departments (ascending order)
                                        allDepartments.forEach((deptName, deptIdx) => {
                                            // Department header row
                                            rows.push(
                                                <TableRow key={`dept-${deptName}`} className="dept-row">
                                                    <TableCell align="left" colSpan={weekDays.length + 4}>
                                                        <Typography variant="body2" fontWeight={600} sx={{ letterSpacing: '0.01em' }}>
                                                            {deptName}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );

                                            // User rows for this department (if any)
                                            const users = departmentGroups[deptName] || [];
                                            if (users.length > 0) {
                                                users.forEach((userData, userIdx) => {
                                                    rows.push(
                                                        <TableRow 
                                                            key={userData.user.id} 
                                                            hover
                                                            sx={{
                                                                '&:last-child td': {
                                                                    borderBottom: deptIdx === allDepartments.length - 1 && userIdx === users.length - 1
                                                                        ? 'none' 
                                                                        : undefined
                                                                }
                                                            }}
                                                        >
                                                            <TableCell className="name-cell">
                                                                <Typography variant="body2" fontWeight={500} sx={{ letterSpacing: '-0.01em' }}>
                                                                    {userData.user.first_name} {userData.user.last_name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="left" className="muted">
                                                                <Typography variant="body2" fontWeight={400}>
                                                                    {userData.user.designation || 'Employee'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center" className="muted">
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 400, fontSize: '0.8125rem' }}>
                                                                    {userData.user.mobile || 'N/A'}
                                                                </Typography>
                                                            </TableCell>
                                                            {weekDays.map((day) => {
                                                                const dateKey = day.format('YYYY-MM-DD');
                                                                const rosterEntry = userData.rosters[dateKey];
                                                                const status = rosterEntry?.status?.name || '-';
                                                                const meta = getStatusMeta(status, rosterEntry);
                                                                return (
                                                                    <TableCell key={dateKey} align="center">
                                                                        <StatusPill color={meta.color}>
                                                                            {meta.label}
                                                                        </StatusPill>
                                                                        {rosterEntry?.event?.event_name && (
                                                                            <Typography variant="body2" fontWeight={400}>
                                                                                {cleanName(rosterEntry.event?.event_name)}
                                                                            </Typography>
                                                                        )}
                                                                        {rosterEntry?.venue?.venue_name && (
                                                                            <Typography variant="body2" fontWeight={400}>
                                                                                {cleanName(rosterEntry.venue?.venue_name) + ' - ' + cleanName(rosterEntry.location?.location_name)}
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    );
                                                });
                                            } else {
                                                // No users in this department - show empty row
                                                rows.push(
                                                    <TableRow key={`empty-${deptName}`}>
                                                        <TableCell className="name-cell">
                                                            <Typography variant="body2" color="text.secondary" fontStyle="italic" fontWeight={400}>
                                                                No employees assigned
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" className="muted">-</TableCell>
                                                        <TableCell align="center" className="muted">-</TableCell>
                                                        {weekDays.map((day) => (
                                                            <TableCell key={day.format('YYYY-MM-DD')} align="center">
                                                                <StatusPill color="muted">-</StatusPill>
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                );
                                            }
                                        });

                                        return rows;
                                    })()}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    </CardContent>
                </StyledCard>
            </Grid>
        </Grid>
    );
});
